import type {
  CreateGenericTaskRequest,
  CreateGenericTaskResponse,
  GenericTaskType,
  GetGenericTaskResponse,
  GetGenericTasksResponse,
} from '@compass/client/types/generic_task';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { genericTaskService } from '@/services/api';
import { useStore } from '@/store/store';

/**
 * Hook to fetch all generic tasks for a case record
 */
export const useGenericTasks = (
  caseRecordId: string | null,
  options?: { refetchInterval?: number | false; limit?: number; type?: GenericTaskType },
) => {
  const testModeEnabled = useStore(state => state.testModeEnabled);
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  const enabled =
    !!caseRecordId && !!selectedOrganizationId && !!selectedProgramId && !testModeEnabled;

  return useQuery<GetGenericTasksResponse>({
    queryKey: ['genericTasks', caseRecordId, testModeEnabled, options?.limit, options?.type],
    queryFn: async () => {
      // Return empty response if in test mode
      if (testModeEnabled) {
        return { tasks: [] };
      }

      return genericTaskService.getGenericTasks({
        organization_id: selectedOrganizationId!,
        program_id: selectedProgramId!,
        case_record_id: caseRecordId!,
        limit: options?.limit,
        type: options?.type,
      });
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: options?.refetchInterval,
  });
};

/**
 * Hook to fetch a single generic task
 */
export const useGenericTask = (
  caseRecordId: string | null,
  taskId: string | null,
  options?: { refetchInterval?: number | false },
) => {
  const testModeEnabled = useStore(state => state.testModeEnabled);
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  const enabled =
    !!caseRecordId &&
    !!selectedOrganizationId &&
    !!selectedProgramId &&
    !!taskId &&
    !testModeEnabled;

  return useQuery<GetGenericTaskResponse>({
    queryKey: ['genericTask', caseRecordId, taskId, testModeEnabled],
    queryFn: async () => {
      // Return empty response if in test mode
      if (testModeEnabled) {
        const now = new Date().toISOString();
        return {
          id: taskId || '',
          case_id: caseRecordId || '',
          type: '',
          status: 'SCHEDULED',
          created_at: now,
          updated_at: now,
        } as GetGenericTaskResponse;
      }

      return genericTaskService.getGenericTask({
        organization_id: selectedOrganizationId!,
        program_id: selectedProgramId!,
        case_record_id: caseRecordId!,
        generic_task_id: taskId!,
      });
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: options?.refetchInterval,
  });
};

/**
 * Hook to create a new generic task
 */
export const useCreateGenericTask = () => {
  const queryClient = useQueryClient();
  const testModeEnabled = useStore(state => state.testModeEnabled);
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  return useMutation<
    CreateGenericTaskResponse,
    Error,
    {
      caseRecordId: string;
      data: Omit<CreateGenericTaskRequest, 'organization_id' | 'program_id' | 'case_record_id'>;
    }
  >({
    mutationFn: async ({ caseRecordId, data }) => {
      if (testModeEnabled) {
        throw new Error('Generic tasks are not supported in test mode');
      }
      return genericTaskService.createGenericTask({
        organization_id: selectedOrganizationId!,
        program_id: selectedProgramId!,
        case_record_id: caseRecordId,
        ...data,
      });
    },
    onSuccess: (_data, variables) => {
      // Invalidate generic tasks list to include new task
      queryClient.invalidateQueries({
        queryKey: ['genericTasks', variables.caseRecordId],
      });
    },
  });
};
